import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, TextField } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import type { IManufacturer, IResponseWithLoading } from "App/Modules/Models";
import type { IManufacturersRepository } from "App/Modules/Repositories";
import { extendWithLoadingAndFail } from "App/Modules/Services";

type EditManufacturerProps = {
    manufacturersRepository: IManufacturersRepository;
    manufacturer: IManufacturer | null;
    onClose: () => void;
    onEditSucess?: (manufacturer: IManufacturer) => void;
};

export const EditManufacturer = (props: EditManufacturerProps) => {
    const { t } = useTranslation();

    const [name, setName] = useState("");
    const [corporateIdNumber, setCorporateIdNumber] = useState("");
    const [vatId, setVatId] = useState("");
    const [nameValid, setNameValid] = useState(true);
    const [saveError, setSaveError] = useState(false);

    useEffect(() => {
        if (props.manufacturer) {
            setName(props.manufacturer.name || "");
            setCorporateIdNumber(props.manufacturer.corporateIdNumber || "");
            setVatId(props.manufacturer.vatId || "");
        }
    }, [props.manufacturer]);

    const validateName = () => {
        let valid = true;
        if (name.length === 0) {
            valid = false;
        }
        setNameValid(valid);
        return valid;
    };

    const validateForm = () => validateName();

    const updateManufacturer = () => {
        setSaveError(false);

        const patchObject = {
            name,
            corporateIdNumber,
            vatId,
        } as IManufacturer;

        extendWithLoadingAndFail(
            props.manufacturersRepository.patchManufacturer(props.manufacturer.id, patchObject),
        ).subscribe(
            (response: IResponseWithLoading<IManufacturer>) => {
                if (!response.isLoading && response.data) {
                    if (props.onEditSucess) {
                        props.onEditSucess(response.data);
                    }
                    props.onClose();
                }
            },
            () => {
                setSaveError(true);
            },
        );
    };

    return (
        <div className="card dialog">
            <form className="dialog__content form">
                <div className="dialog__section dialog__section--full">
                    <h1 className="title title--h1 title--no-spacing">{t("Entities.Manufacturer.EditManufacturer")}</h1>
                </div>

                <div className="dialog__section dialog__section--full">
                    <label className="caption" htmlFor="name">
                        {t("Entities.Common.Name")}
                    </label>
                    <TextField
                        required
                        fullWidth
                        id="name"
                        variant="outlined"
                        size="small"
                        type="text"
                        helperText={!nameValid ? t("Validation.Mixed.Required") : null}
                        error={!nameValid}
                        value={name}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                        onBlur={() => validateName()}
                    />
                </div>

                <div className="dialog__section dialog__section--full">
                    <label className="caption" htmlFor="corporate-id-number">
                        {t("Entities.Common.CorporateIdNumber")}
                    </label>
                    <TextField
                        required
                        fullWidth
                        id="corporate-id-number"
                        variant="outlined"
                        size="small"
                        type="text"
                        value={corporateIdNumber}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setCorporateIdNumber(event.target.value)
                        }
                    />
                </div>

                <div className="dialog__section dialog__section--full">
                    <label className="caption" htmlFor="vat-id">
                        {t("Entities.Manufacturer.VatId")}
                    </label>
                    <TextField
                        required
                        fullWidth
                        id="vat-id"
                        variant="outlined"
                        size="small"
                        type="text"
                        value={vatId}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setVatId(event.target.value)}
                    />
                </div>

                {saveError ? (
                    <div className="dialog__section dialog__section--full dialog__section--noscroll">
                        <div className="notice notice--critical">
                            <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                            <p className="caption">{t("Error.ErrorSaving")}</p>
                        </div>
                    </div>
                ) : null}

                <div className="dialog__section dialog__section--buttons">
                    <Button size="small" color="default" onClick={() => props.onClose()}>
                        {t("Gui.Buttons.Cancel")}
                    </Button>

                    <Button
                        type="button"
                        size="small"
                        color="primary"
                        onClick={() => {
                            if (validateForm()) {
                                updateManufacturer();
                            }
                        }}
                    >
                        {t("Gui.Buttons.Save")}
                    </Button>
                </div>
            </form>
        </div>
    );
};
